/* client/src/pages/Feed/GlobalFeed.js */
import React, { useState, useEffect, useContext } from 'react';
import { authFetch } from '../../Utilities/Api';
import AuthContext from '../../context/AuthContext';
import PostItem from '../../components/ProfilePosts/PostItem';
import CreatePostModal from '../../components/ProfilePosts/CreatePostModal';

import './GlobalFeed.css';

const GlobalFeed = () => {
    const { user } = useContext(AuthContext);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // 🔍 DEBUG: Log Component Mount
    useEffect(() => {
        console.log("🏠 [GlobalFeed] Component Rendered. User:", user?.username);
    }, [user]);

    useEffect(() => {
        const fetchFeed = async () => {
            if (!user) return;

            setLoading(true);

            // ✅ THE CORRECT URL: matches server/routes/ProfilePosts/ProfilePosts.js
            const TARGET_URL = '/profile-posts/feed';

            console.log(`📡 [GlobalFeed] Fetching feed from: ${TARGET_URL}`);

            try {
                const res = await authFetch(TARGET_URL);

                console.log(`⬅️ [GlobalFeed] Response Status: ${res.status}`);

                if (!res.ok) {
                    console.error("❌ [GlobalFeed] Fetch Failed:", res.status, res.statusText);
                    setError(true);
                    return;
                }

                const data = await res.json();
                console.log("✅ [GlobalFeed] Data Received:", data);

                // 🛡️ SAFETY CHECK: Handle different API response formats
                if (Array.isArray(data)) {
                    console.log(`📊 [GlobalFeed] Data is an Array with ${data.length} posts. Setting posts.`);
                    setPosts(data);
                } else if (data.posts && Array.isArray(data.posts)) {
                    console.log(`📊 [GlobalFeed] Data is an Object with .posts key (${data.posts.length} items). Setting posts.`);
                    setPosts(data.posts);
                } else {
                    console.warn("⚠️ [GlobalFeed] Unexpected data format. Expected array, got:", data);
                    setPosts([]);
                }

            } catch (err) {
                console.error("❌ [GlobalFeed] Critical Error:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchFeed();
    }, [user]);

    const handlePostDeleted = (deletedPostId) => {
        console.log("🗑️ [GlobalFeed] Post deleted:", deletedPostId);
        setPosts((prev) => prev.filter(p => p._id !== deletedPostId));
    };

    const handlePostCreated = (newPost) => {
        console.log("✨ [GlobalFeed] New Post created:", newPost);
        setPosts((prev) => [newPost, ...prev]);
    };

    if (loading) return <div className="feed-wrapper"><div className="spinner"></div></div>;

    if (error && posts.length === 0) return (
        <div className="feed-wrapper">
            <p className="error-text">Unable to load feed. Please try again later.</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="feed-wrapper">
            <div className="feed-header">
                <h2>Your Feed</h2>
                <p>Latest posts from your friends</p>
            </div>

            <div className="create-post-card">
                <img
                    src={user?.profilePicture || "https://via.placeholder.com/40"}
                    alt="Me"
                    className="mini-avatar"
                />
                <button
                    className="create-trigger-btn"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    What's on your mind, {user?.nickname || user?.username}?
                </button>
            </div>

            <div className="feed-list">
                {posts.length === 0 ? (
                    <div className="empty-feed">
                        <h3>No posts yet!</h3>
                        <p>Add friends to see their posts here.</p>
                    </div>
                ) : (
                    posts.map((post) => (
                        <PostItem
                            key={post._id}
                            post={post}
                            onPostDeleted={handlePostDeleted}
                            showComments={true}
                        />
                    ))
                )}
            </div>

            {isCreateModalOpen && (
                <CreatePostModal
                    user={user}
                    onPostCreated={handlePostCreated}
                    onClose={() => setIsCreateModalOpen(false)}
                />
            )}
        </div>
    );
};

export default GlobalFeed;